import { EventProduct, GetCompletedEventsQuery, GetTodoTasksForVendorsQuery } from '@/api';
import { GetManifestsQuery } from '@/api';
import type { ProductType } from '@/pages/Availability.page/types';
import { CalendarEventStatus } from '../Calendar.page/types';
// Extract the event vendor nodes array type from the query
type EventVendorNodesArray = NonNullable<
  NonNullable<GetCompletedEventsQuery['searchEventVendors']>['eventVendors']
>['nodes'];

// Extract individual event vendor node type
export type SearchEventVendorNode = NonNullable<EventVendorNodesArray>[number];

// Union type for all event vendor types (removes null)
export type SearchEvent = NonNullable<SearchEventVendorNode>;

// Individual event vendor type guards
export type SearchPopupEvent = Extract<SearchEvent, { __typename: 'PopupEventVendor' }>;
export type SearchCafeEvent = Extract<SearchEvent, { __typename: 'PopupEventVendor' }>;
export type SearchDeliveryEvent = Extract<SearchEvent, { __typename: 'SelectEventVendor' }>;
export type SearchCateringEvent = Extract<SearchEvent, { __typename: 'CateringEventVendor' }>;

export type TSearchEvent =
  | SearchPopupEvent
  | SearchCafeEvent
  | SearchDeliveryEvent
  | SearchCateringEvent;

export type EventPreview = {
  id: string;
  eventTime: string;
  eventType: EventProduct;
  restaurantName: string;
  accountName: string;
  eventDetailsUrl: string;
  cateringEventName?: string;
  address?: string;
  setupTime?: string;
  manifestTime?: string;
  readyAndBaggedTime?: string;
  vendorId: string;
  status: CalendarEventStatus | null;
  productTypeId: string;
};

// Union type for event locations (only Popup and Cafe have eventLocation)
export type EventVendorLocation =
  | NonNullable<SearchPopupEvent['eventLocation']>
  | NonNullable<SearchCateringEvent['event']>
  | NonNullable<SearchDeliveryEvent['selectEvent']>;

export type TodoTask = NonNullable<
  NonNullable<GetTodoTasksForVendorsQuery['getTodoTasksForVendors']>['todoTasks']
>[number];

export type TodoTaskWorkItem = TodoTask['workItems'][number];

export type TodoTaskEventVendor = Extract<TodoTaskWorkItem, { __typename: 'PopupEventVendor' }>;

export type TodoTaskAvailabilityRequirement = Extract<
  TodoTaskWorkItem,
  { __typename: 'SchedulingAvailabilityRequirement' }
>;

export type AvailabilitySummary = {
  productType: ProductType;
  restaurants: { vendorId: string; internalName: string }[];
  expiresAt: string;
};

export type AvailabilitySummaryAccumulator = {
  restaurants: AvailabilitySummary['restaurants'];
  seen: Set<string>;
  expiresAt: string;
};

export type TodoBucketItem =
  | { type: 'task'; task: TodoTask }
  | { type: 'summary'; summary: AvailabilitySummary };

export type DateTasks = {
  date: string;
  items: TodoBucketItem[];
};

export type DateEvents = {
  date: string;
  events: EventPreview[];
};

export enum DashboardEventDetailsUrl {
  POPUP_UPCOMING = '/dashboard/popup_upcoming/',
  POPUP_COMPLETED = '/dashboard/popup_completed/',
  CAFE_UPCOMING = '/dashboard/popup_upcoming/',
  CAFE_COMPLETED = '/dashboard/popup_completed/',
  DELIVERY_UPCOMING = '/dashboard/delivery_upcoming/',
  DELIVERY_COMPLETED = '/dashboard/delivery_completed/',
  CATERING_UPCOMING = '/dashboard/catering_upcoming/',
  CATERING_COMPLETED = '/dashboard/catering_completed/',
}

export type DeliveryManifest = NonNullable<
  NonNullable<GetManifestsQuery['getTodayDeliveryManifests']>['deliveryManifests']
>[number];
